import { action, computed, makeObservable, observable } from "mobx";
import { Dictionary } from "../../logic/Dictionaries";
import { Fields } from "./Fields";
import { FormInputItem, IFormInputItem, isInputItem } from "./FormInputItem";
import { IFormPage, FormPage } from "./FormPage";

export interface IForm {
    version: number;
    title: string;
    description?: string;
    pages: IFormPage[];
}

export class Form extends Fields<IFormInputItem, FormInputItem> implements IForm {
    @observable version: number;
    @observable title: string;
    @observable description?: string;
    @observable pages: FormPage[];

    @observable private currentPageIndex: number = 0;
    @observable pageHistory: string[] = [];

    constructor(form: IForm) {
        super();
        makeObservable(this);

        this.version = form.version;
        this.title = form.title;
        this.description = form.description;
        this.pages = form.pages.map(section => new FormPage(section));
        this.pageHistory = [form.pages[0].id];
    }

    setAllFieldsDirty() {
        this.pagesTraversed.forEach(page => page.setAllFieldsDirty());
    }

    @computed get formData() {
        const data = { version: this.version.toString(), pageHistory: this.pageIndexHistory.join(",") } as Dictionary<string, string | string[]>;
        this.inputFieldsTraversed.forEach(inputField => {
            if (inputField.value !== undefined) {
                data[inputField.id] = inputField.value
            }
            if (inputField.otherSelected && inputField.otherValue?.trim().length !== 0) {
                if (data[inputField.id] !== undefined && Array.isArray(data[inputField.id])) {
                    data[inputField.id] = (data[inputField.id] as string[]).concat('__other_option__');
                } else {
                    data[inputField.id] = ['__other_option__'];
                }
                data[`${inputField.id}.other_option_response`] = inputField.otherValue || '';
            }
        })
        return data;
    }

    @computed private get pageIndexHistory() {
        const pageIndexes = [] as number[];
        this.pageHistory.forEach(pageId => {
            const pageIndex = this.pages.findIndex(page => page.id === pageId);
            if (pageIndex !== -1) {
                pageIndexes.push(pageIndex);
            }
        });
        return pageIndexes;
    }

    @computed private get pagesTraversed() {
        const pages = [] as FormPage[];
        this.pageHistory.forEach(pageId => {
            const page = this.pages.find(page => page.id === pageId);
            if (page) {
                pages.push(page);
            }
        });
        return pages;
    }

    @computed get validationErrors() {
        return this.inputFieldsTraversed.flatMap(inputField => inputField.validationErrors);
    }

    @computed private get inputFieldsTraversed() {
        return this.pagesTraversed.flatMap(page => page.sections).flatMap(section => section.items).filter(item => isInputItem(item)) as FormInputItem[];
    }

    @action private setCurrentPageIndex(page: number) {
        this.currentPageIndex = page;
    }

    @computed get currentPage() {
        return this.pages[this.currentPageIndex];
    }

    @action advancePage() {
        if (this.onSubmittablePage) {
            return;
        }

        // Don't advance if there are errors
        this.currentPage.setAllFieldsDirty();
        if (this.currentPage.validationErrors.length > 0) {
            return;
        }

        const nextPage = this.pages[this.currentPageIndex].advanceTo;
        let indexOfNextPage = this.currentPageIndex + 1;
        if ('stepId' in nextPage) {
            indexOfNextPage = this.pages.findIndex(page => page.id === nextPage.stepId);
        }
        this.setCurrentPageIndex(indexOfNextPage);
        const idOfNextPage = this.pages[indexOfNextPage].id;
        this.pageHistory = this.pageHistory.concat(idOfNextPage);
    }

    @action navigateBack() {
        if (!this.onRetreatablePage) {
            return;
        }
        const previousPage = this.pageHistory[this.pageHistory.length - 2];
        const indexOfPreviousPage = this.pages.findIndex(page => page.id === previousPage);
        this.setCurrentPageIndex(indexOfPreviousPage);
        this.pageHistory = this.pageHistory.slice(0, -1);
    }

    @computed get onSubmittablePage() {
        return this.pages[this.currentPageIndex].advanceTo.type === 'end';
    }

    @computed get onRetreatablePage() {
        return this.pageHistory.length > 1;
    }
}